
'use client';

import { useCallback, useState, useEffect, useRef } from 'react';
import type { Anniversary, AnniversaryInput } from '@/lib/types';
import { useToast } from './use-toast';
import {
  useFirestore,
  useUser,
  useMemoFirebase,
  useAuth,
} from '@/firebase';
import {
  collection,
  doc,
  serverTimestamp,
  Query,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import {
  addDocumentNonBlocking,
  deleteDocumentNonBlocking,
  setDocumentNonBlocking,
} from '@/firebase/non-blocking-updates';
import { signInAnonymously } from 'firebase/auth';

async function ensureAuthenticated(auth: any, user: any) {
  if (user) {
    return user;
  }
  const userCredential = await signInAnonymously(auth);
  return userCredential.user;
}

export function useAnniversaries() {
  const { toast } = useToast();
  const auth = useAuth();
  const firestore = useFirestore();
  const { user, isUserLoading } = useUser();
  const [anniversaries, setAnniversaries] = useState<Anniversary[]>([]);

  const anniversariesCollectionRef = useMemoFirebase(() => {
    if (!firestore || !user || isUserLoading) return null;
    return collection(firestore, 'users', user.uid, 'anniversaries');
  }, [firestore, user, isUserLoading]);

  useEffect(() => {
    if (!anniversariesCollectionRef) {
      setAnniversaries([]); // Clear data on logout
      return;
    }

    const unsubscribe = onSnapshot(anniversariesCollectionRef as Query, (snapshot) => {
      const serverData = snapshot.docs.map(doc => ({ ...doc.data() as Anniversary, id: doc.id }));
      setAnniversaries(serverData);
    }, (error) => {
      console.error("Error listening to anniversary updates:", error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not fetch anniversary data.',
      });
    });

    return () => unsubscribe();
  }, [anniversariesCollectionRef, toast]);

  const addAnniversary = useCallback(
    async (anniversaryInput: AnniversaryInput) => {
      try {
        const currentUser = await ensureAuthenticated(auth, user);
        if (!firestore || !currentUser) {
            throw new Error("User not authenticated.");
        }
        const ref = collection(firestore, 'users', currentUser.uid, 'anniversaries');

        const newAnniversary = {
            ...anniversaryInput,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        };
        addDocumentNonBlocking(ref, newAnniversary);
        toast({ title: '🎉 Anniversary added!', description: `You've added the ${anniversaryInput.name} anniversary.` });

      } catch (error) {
        console.error("Error adding anniversary:", error);
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Could not add anniversary. Please try again.',
        });
      }
    },
    [auth, user, firestore, toast]
  );

  const updateAnniversary = useCallback(
    async (updatedAnniversary: Anniversary) => {
       try {
        const currentUser = await ensureAuthenticated(auth, user);
        if (!firestore || !currentUser) {
            throw new Error("User not authenticated.");
        }
        const anniversaryDocRef = doc(
            firestore,
            'users',
            currentUser.uid,
            'anniversaries',
            updatedAnniversary.id
        );
        const updateData = {
            ...updatedAnniversary,
            updatedAt: serverTimestamp(),
        };
        // Omit id from the data being sent to Firestore
        const { id, ...dataToUpdate } = updateData;
        setDocumentNonBlocking(anniversaryDocRef, dataToUpdate, { merge: true });
        toast({ title: '💖 Anniversary updated!', description: `The ${updatedAnniversary.name} anniversary has been updated.` });
      } catch (error) {
        console.error("Error updating anniversary:", error);
        toast({
            variant: 'destructive',
            title: 'Error',
            description: 'Could not update anniversary. Please try again.',
        });
      }
    },
    [auth, user, firestore, toast]
  );

  const deleteAnniversary = useCallback(
    async (id: string) => {
       try {
        const currentUser = await ensureAuthenticated(auth, user);
        if (!firestore || !currentUser) {
            throw new Error("User not authenticated.");
        }
        const anniversaryDocRef = doc(firestore, 'users', currentUser.uid, 'anniversaries', id);
        deleteDocumentNonBlocking(anniversaryDocRef);
        toast({ title: '🗑️ Anniversary deleted.' });
      } catch (error) {
        console.error("Error deleting anniversary:", error);
        toast({
            variant: 'destructive',
            title: 'Error',
            description: 'Could not delete anniversary. Please try again.',
        });
      }
    },
    [auth, user, firestore, toast]
  );

  return {
    anniversaries: anniversaries ?? [],
    addAnniversary,
    updateAnniversary,
    deleteAnniversary,
  };
}
